/**
 * Una linea tipeada. Arranca por el producto, no por la descripcion.
 *
 * Cuando la linea la escribe una persona (compra sin remito, o la IA no pudo
 * leer el archivo), esa persona casi siempre sabe que producto es. Preguntarle
 * "como dice el papel" para despues adivinar el producto por parecido y
 * repreguntarle "¿es este?" es hacerla contestar dos veces. Aca busca en el
 * catalogo primero; la linea llega con el producto elegido, decidida, y el
 * wizard no vuelve a preguntar por ella.
 *
 * Si el producto no existe, "No esta en el catalogo" cambia a la descripcion
 * libre: la linea entra sin producto y el wizard ofrece crearlo, como siempre.
 */
import { useState, type KeyboardEvent } from "react";
import { Check, PlusCircle, X } from "lucide-react";
import { convertir, formatear, formatearNumero, type Moneda } from "@tora/shared";

import { Campo, CampoCantidad } from "@/components/catalogo/Campos";
import { Importe, InputImporte } from "@/components/money";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import { Codigo } from "@/components/ui/codigo";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { useDebounce } from "@/hooks/use-debounce";
import { useProductos } from "@/lib/queries";
import { aNumero, type Producto } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import type { NuevaFila } from "./consultas";
import { SALTO_DE_PRECIO } from "./logica";

export interface LineaManualProps {
  moneda: Moneda;
  /** El dolar de la factura, para mostrar el ultimo costo en su moneda. */
  cotizacion?: number;
  /** Para buscar primero entre los productos de este proveedor. */
  proveedorId: number | null;
  guardando: boolean;
  onAgregar: (fila: NuevaFila) => Promise<void>;
  onCancelar: () => void;
}

export function LineaManual({
  moneda: monedaInicial,
  cotizacion = 0,
  proveedorId,
  guardando,
  onAgregar,
  onCancelar,
}: LineaManualProps): JSX.Element {
  const [producto, setProducto] = useState<Producto | null>(null);
  const [sinCatalogo, setSinCatalogo] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [codigo, setCodigo] = useState("");
  const [cantidad, setCantidad] = useState<number | null>(1);
  const [neto, setNeto] = useState<number | null>(null);
  const [moneda, setMoneda] = useState<Moneda>(monedaInicial);
  const [iva, setIva] = useState("0");
  // Cambia en cada alta: remonta el buscador vacio y con el foco puesto.
  const [buscador, setBuscador] = useState(0);

  const queProducto = producto !== null || (sinCatalogo && descripcion.trim() !== "");
  const listo = queProducto && cantidad !== null && cantidad > 0 && neto !== null && neto >= 0;

  function elegir(p: Producto): void {
    setProducto(p);
    setSinCatalogo(false);
    // Lo ultimo que se pago por el es el punto de partida, pero SOLO si esta
    // en la moneda de esta linea. Antes se tomaba el costo con su moneda: en
    // una compra en dolares, el cargador de $14.800 pasaba el renglon a pesos,
    // se tipeaba 9,5 pensando en dolares y quedaba $9,50, con la deuda en
    // cero. La moneda la pone la factura; el costo en otra moneda queda como
    // referencia abajo del campo.
    if (neto === null && aNumero(p.costo) > 0 && p.costo_moneda === moneda) {
      setNeto(aNumero(p.costo));
    }
  }

  /**
   * El ultimo costo, como referencia debajo del precio. Si esta en otra moneda
   * que la linea, convertido con el dolar de la factura (si lo hay).
   *
   * Ademas reserva el renglon de abajo del campo: sin ayuda, el eco «= $ …»
   * que aparece mientras se tipea desaparecia al salir del campo y todo lo de
   * abajo subia; el clic en «Agregar» empezaba sobre el boton y terminaba
   * debajo, y el renglon no se agregaba sin ningun aviso.
   */
  function referenciaDeCosto(): string {
    if (producto === null || aNumero(producto.costo) <= 0) return "Sin costo anterior.";
    const costo = aNumero(producto.costo);
    if (producto.costo_moneda === moneda) {
      // Un precio que se aparta mas de la mitad del ultimo costo casi siempre
      // esta mal tipeado: $ 5 en vez de $ 2.800 bajaba el precio de venta un
      // 99,8% y solo se veia el costo viejo, fijo, debajo del campo.
      const conIva = neto === null ? null : neto * (1 + Number(iva) / 100);
      const variacion = conIva === null ? 0 : (conIva - costo) / costo;
      const salto =
        Math.abs(variacion) > SALTO_DE_PRECIO
          ? ` Este es un ${formatearNumero(Math.abs(variacion) * 100, 1)} % ${variacion > 0 ? "más alto" : "más bajo"}: revisá que esté bien.`
          : "";
      return `Último costo: ${formatear(costo, moneda)}.${salto}`;
    }
    const enLinea =
      cotizacion > 0
        ? ` (≈ ${formatear(convertir(costo, producto.costo_moneda, moneda, cotizacion), moneda)})`
        : "";
    return `Último costo: ${formatear(costo, producto.costo_moneda)}${enLinea}.`;
  }

  /** Despues de agregar, el formulario vuelve a empezar para la linea siguiente. */
  function limpiar(): void {
    setProducto(null);
    setSinCatalogo(false);
    setDescripcion("");
    setCodigo("");
    setCantidad(1);
    setNeto(null);
    setIva("0");
    setBuscador((n) => n + 1);
  }

  async function agregar(): Promise<void> {
    try {
      await onAgregar({
        producto_id: producto?.id ?? null,
        descripcion: producto ? producto.nombre : descripcion.trim(),
        codigo_externo: producto
          ? (producto.codigo_externo ?? null)
          : codigo.trim() === ""
            ? null
            : codigo.trim(),
        cantidad: cantidad ?? 1,
        costo_neto: neto ?? 0,
        iva_pct: Number(iva),
        moneda,
      });
    } catch {
      // El error ya lo mostro quien agrega; lo cargado queda para corregir.
      return;
    }
    // Antes el formulario quedaba con el mismo producto, cantidad y precio:
    // parecia que no se habia agregado, y un segundo clic duplicaba el renglon.
    limpiar();
  }

  return (
    // Un formulario y no una tarjeta suelta: en el iPhone «Agregar» quedaba
    // debajo del teclado en cada renglón, y así la tecla «Ir» del teclado
    // agrega sin tener que bajarlo.
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (listo && !guardando) void agregar();
      }}
    >
      <Card padding="normal" className="space-y-4">
        <TituloSeccion>Agregar un producto</TituloSeccion>

        {producto ? (
          <Card
            tono="success"
            padding="none"
            className="flex items-center justify-between gap-3 px-3 py-2"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Check className="size-4 shrink-0 text-success" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block break-words text-sm font-medium sm:truncate">
                  {producto.nombre}
                </span>
                <span className="block truncate text-xs tabular-nums text-muted-foreground">
                  <Codigo>{producto.codigo_interno}</Codigo> · stock{" "}
                  {aNumero(producto.stock_actual)} · costo{" "}
                  <Importe
                    monto={aNumero(producto.costo)}
                    moneda={producto.costo_moneda}
                    tamano="sm"
                  />
                </span>
              </span>
            </span>
            <IconButton
              icono={X}
              aria-label="Cambiar de producto"
              tamano="sm"
              onClick={() => setProducto(null)}
            />
          </Card>
        ) : sinCatalogo ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Campo label="Artículo, como dice el papel" id="manual-desc">
                <Input
                  id="manual-desc"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  autoComplete="off"
                  autoFocus
                />
              </Campo>
            </div>
            <Campo label="Código del proveedor (opcional)" id="manual-codigo">
              <Input
                id="manual-codigo"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                autoComplete="off"
              />
            </Campo>
            <p className="self-end text-xs text-muted-foreground">
              Entra sin producto: en el paso siguiente el sistema te ofrece crearlo.{" "}
              <Button
                type="button"
                variant="link"
                className="text-xs"
                onClick={() => setSinCatalogo(false)}
              >
                Volver a buscar
              </Button>
            </p>
          </div>
        ) : (
          <BuscadorProducto
            key={buscador}
            proveedorId={proveedorId}
            onElegir={elegir}
            onNoEsta={(tipeado) => {
              // Lo que ya escribio no se vuelve a tipear.
              if (tipeado !== "" && descripcion.trim() === "") setDescripcion(tipeado);
              setSinCatalogo(true);
            }}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoCantidad
            label="Cantidad"
            valor={cantidad}
            onValorChange={setCantidad}
            sufijo="u."
            requerido
          />
          <InputImporte
            label="Precio unitario, como figura"
            valor={neto}
            onValorChange={setNeto}
            moneda={moneda}
            onMonedaChange={setMoneda}
            convertirAlCambiarMoneda={false}
            ayuda={referenciaDeCosto()}
            requerido
          />
          <Campo label="IVA discriminado" id="manual-iva">
            <Select value={iva} onValueChange={setIva}>
              <SelectTrigger id="manual-iva">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No discrimina (precio final)</SelectItem>
                <SelectItem value="10.5">10,5 %</SelectItem>
                <SelectItem value="21">21 %</SelectItem>
                <SelectItem value="27">27 %</SelectItem>
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancelar} disabled={guardando}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!listo} cargando={guardando}>
            Agregar
          </Button>
        </div>
      </Card>
    </form>
  );
}

/**
 * El buscador del catalogo, como un autocompletado: se tipea, la lista se
 * despliega pegada al campo y se elige con flechas y Enter (o con un click).
 * Con el campo vacio ya muestra los primeros del proveedor, que en un
 * catalogo chico suele alcanzar sin tipear nada.
 */
function BuscadorProducto({
  proveedorId,
  onElegir,
  onNoEsta,
}: {
  proveedorId: number | null;
  onElegir: (p: Producto) => void;
  onNoEsta: (textoTipeado: string) => void;
}): JSX.Element {
  const [texto, setTexto] = useState("");
  // Cerrada al montar: abierta, la lista tapaba «Ir a revisar». Se abre al
  // enfocar o al tipear.
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const [soloDelProveedor, setSoloDelProveedor] = useState(proveedorId !== null);
  const consulta = useDebounce(texto, 250).trim();

  const filtrarPorProveedor = soloDelProveedor && proveedorId !== null;
  const { data, isFetching } = useProductos({
    ...(consulta === "" ? {} : { q: consulta }),
    ...(filtrarPorProveedor ? { proveedor_id: proveedorId } : {}),
    activo: true,
    limite: 8,
  });
  // La misma busqueda en todo el catalogo. Los iPhone sellados no tienen
  // proveedor: con el filtro prendido «iphone 15 128» no traia nada, la lista
  // decia «No está en el catálogo» y seguirla creaba un duplicado que no
  // cerraba el encargo. Sin filtro la clave es la misma y no se pide dos veces.
  const { data: dataTodos, isFetching: buscandoTodos } = useProductos({
    ...(consulta === "" ? {} : { q: consulta }),
    activo: true,
    limite: 8,
  });
  const propios = data?.filas ?? [];
  const deOtros = filtrarPorProveedor && consulta !== "" && propios.length === 0 && !isFetching;
  const productos = deOtros ? (dataTodos?.filas ?? []) : propios;
  const buscando = isFetching || (deOtros && buscandoTodos);
  // Enter elige «No está en el catálogo» solo si se llego con las flechas.
  // Antes, sin resultados, Enter lo elegia solo y dejaba la linea sin producto.
  const [conFlechas, setConFlechas] = useState(false);
  // La ultima opcion (indice = productos.length) es "no esta en el catalogo".
  const indiceActivo = Math.min(activo, productos.length);

  function elegir(p: Producto): void {
    setAbierto(false);
    onElegir(p);
  }

  function teclas(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAbierto(true);
      setConFlechas(true);
      setActivo((i) => Math.min(i + 1, productos.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setConFlechas(true);
      setActivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Siempre se frena: el buscador vive adentro del formulario del renglón,
      // y un Enter acá es para elegir un producto, no para agregar la línea.
      e.preventDefault();
      if (!abierto) return;
      const p = productos[indiceActivo];
      if (p) elegir(p);
      else if (conFlechas) onNoEsta(texto.trim());
    } else if (e.key === "Escape") {
      setAbierto(false);
    }
  }

  const mostrarLista = abierto;

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="manual-buscar">Producto</Label>
          {proveedorId !== null ? (
            <Checkbox
              label="Solo de este proveedor"
              checked={soloDelProveedor}
              onChange={(e) => setSoloDelProveedor(e.target.checked)}
              className="items-center"
            />
          ) : null}
        </div>
        <div className="relative">
          {/* El campo es un combobox: el nombre accesible lo da la Label de
              arriba (la etiqueta del buscador no la pisa porque va como
              aria-label del mismo elemento, y dice lo mismo). */}
          <InputBusqueda
            id="manual-buscar"
            etiqueta="Producto"
            role="combobox"
            aria-expanded={mostrarLista}
            aria-controls="manual-buscar-lista"
            aria-autocomplete="list"
            aria-activedescendant={
              !mostrarLista
                ? undefined
                : productos[indiceActivo]
                  ? `manual-opcion-${productos[indiceActivo].id}`
                  : "manual-opcion-nuevo"
            }
            valor={texto}
            onValorChange={(v) => {
              setTexto(v);
              setActivo(0);
              setConFlechas(false);
              setAbierto(true);
            }}
            // Con un clic se abre; el foco solo no: el campo arranca enfocado
            // (autoFocus) y abrir al enfocar desplegaba la lista sola.
            onClick={() => setAbierto(true)}
            // El click en una opcion dispara blur antes que click: se cierra
            // recien despues, para que la opcion alcance a elegirse.
            onBlur={() => setTimeout(() => setAbierto(false), 150)}
            onKeyDown={teclas}
            placeholder="Empezá a escribir el nombre o el código"
            autoFocus
          />

          {mostrarLista ? (
            // Listbox a mano: las opciones no reciben foco (el combobox lo
            // conserva y las senala con aria-activedescendant), asi que el
            // resaltado de la activa hace de anillo de foco.
            <ul
              id="manual-buscar-lista"
              role="listbox"
              aria-label="Productos encontrados"
              className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
            >
              {deOtros && productos.length > 0 ? (
                <li
                  role="presentation"
                  className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground"
                >
                  De este proveedor no hay nada con eso. De otros proveedores o sin proveedor:
                </li>
              ) : null}
              {productos.length === 0 ? (
                <li className="flex justify-center p-3">
                  {buscando ? (
                    <Cargando tamano="inline" texto="Buscando…" />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No encontramos ningún producto con eso.
                    </span>
                  )}
                </li>
              ) : (
                productos.map((p, i) => (
                  <li
                    key={p.id}
                    id={`manual-opcion-${p.id}`}
                    role="option"
                    aria-selected={i === indiceActivo}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActivo(i)}
                    onClick={() => elegir(p)}
                    className={cn(
                      "flex cursor-pointer items-start justify-between gap-3 rounded-sm px-3 py-2 text-left transition-colors duration-100",
                      i === indiceActivo ? "bg-accent" : "hover:bg-accent",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block break-words text-sm font-medium sm:truncate">
                        {p.nombre}
                      </span>
                      <span className="block truncate text-xs tabular-nums text-muted-foreground">
                        <Codigo>{p.codigo_interno}</Codigo>
                        {p.codigo_externo ? (
                          <>
                            {" "}
                            · <Codigo>{p.codigo_externo}</Codigo>
                          </>
                        ) : null}{" "}
                        · stock {aNumero(p.stock_actual)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs text-muted-foreground">
                      costo <Importe monto={aNumero(p.costo)} moneda={p.costo_moneda} tamano="sm" />
                    </span>
                  </li>
                ))
              )}
              {/* Siempre al final, aunque haya resultados: el producto nuevo. */}
              <li
                id="manual-opcion-nuevo"
                role="option"
                aria-selected={indiceActivo === productos.length}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActivo(productos.length)}
                onClick={() => onNoEsta(texto.trim())}
                className={cn(
                  "mt-1 flex cursor-pointer items-center gap-2 rounded-sm border-t border-border px-3 py-2 text-sm transition-colors duration-100",
                  indiceActivo === productos.length ? "bg-accent" : "hover:bg-accent",
                )}
              >
                <PlusCircle className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span>
                  {texto.trim() === "" ? (
                    "No está en el catálogo: cargarlo como producto nuevo"
                  ) : (
                    <>
                      No está en el catálogo: cargar «<strong>{texto.trim()}</strong>» como nuevo
                    </>
                  )}
                </span>
              </li>
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}
